import { Dismiss20Regular } from "@fluentui/react-icons";
import { useEffect, useRef, useState } from "react";
import { createWork, type Work } from "../../obras/obrasApi";
import { SearchableSelect } from "../SearchableSelect";
import { useLookupOptions } from "../../lookups/useLookupOptions";

interface NovaObraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (work: Work) => void;
}

export function NovaObraModal({ isOpen, onClose, onCreated }: NovaObraModalProps) {
  const {
    options: clienteOptions,
    loading: isLoadingClients,
    error: clientsError,
  } = useLookupOptions("clients", { enabled: isOpen });
  const [nome, setNome] = useState("");
  const [clientId, setClientId] = useState<number | "">("");
  const [localizacao, setLocalizacao] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [prazo, setPrazo] = useState("");
  const [valorTotal, setValorTotal] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nomeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    setNome("");
    setClientId("");
    setLocalizacao("");
    setDataInicio("");
    setPrazo("");
    setValorTotal("");
    setError(null);
    setIsSubmitting(false);

    nomeInputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const parsedValor = valorTotal.trim() === "" ? null : Number(valorTotal);
  const isValorValido = parsedValor === null || (Number.isFinite(parsedValor) && parsedValor >= 0);
  const isFormValido = nome.trim().length > 0 && clientId !== "" && isValorValido;

  const handleCriar = async () => {
    if (!isFormValido || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const work = await createWork({
        name: nome.trim(),
        clientId: Number(clientId),
        location: localizacao.trim() || null,
        startDate: dataInicio || null,
        endDate: prazo || null,
        totalValue: parsedValor,
      });
      onCreated(work);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Não foi possível criar a obra.",
      );
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50" />

      {/* Modal */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="nova-obra-modal-title"
          className="bg-card border border-border shadow-lg w-full max-w-[560px] max-h-[90vh] flex flex-col"
          style={{ borderRadius: "var(--radius-card)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-8 pb-6 border-b border-border">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h2
                  id="nova-obra-modal-title"
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-large)",
                    fontWeight: "var(--font-weight-semi-bold)",
                    lineHeight: "1.3",
                  }}
                >
                  Nova Obra
                </h2>
                <p
                  className="text-secondary-text mt-2"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Crie uma obra diretamente. Também pode criar obras a partir de
                  orçamentos aceites, na página de Orçamentos.
                </p>
              </div>
              <button
                onClick={onClose}
                aria-label="Fechar"
                className="ml-4 p-2 hover:bg-accent rounded transition-colors"
                style={{ borderRadius: "var(--radius)" }}
              >
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Body - Scrollable */}
          <div className="flex-1 overflow-y-auto p-8 space-y-6">
            {(error || clientsError) && (
              <div
                className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
                style={{ borderRadius: "var(--radius)" }}
              >
                {error ?? "Não foi possível carregar os clientes."}
              </div>
            )}

            <div>
              <label
                className="text-foreground block mb-2"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Nome da Obra <span className="text-destructive">*</span>
              </label>
              <input
                ref={nomeInputRef}
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Remodelação de moradia — Cascais"
                className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors"
                style={{
                  borderRadius: "var(--radius-button)",
                  fontSize: "var(--text-base)",
                }}
              />
            </div>

            <div>
              <label
                className="text-foreground block mb-2"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Cliente <span className="text-destructive">*</span>
              </label>
              <SearchableSelect<number>
                value={clientId === "" ? null : clientId}
                onChange={(next) => setClientId(next ?? "")}
                options={clienteOptions}
                loading={isLoadingClients}
                disabled={isLoadingClients}
                placeholder={
                  isLoadingClients ? "A carregar clientes..." : "Selecionar cliente"
                }
                searchPlaceholder="Pesquisar cliente..."
                ariaLabel="Cliente"
              />
            </div>

            <div>
              <label
                className="text-foreground block mb-2"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Localização <span className="text-secondary-text">(opcional)</span>
              </label>
              <input
                type="text"
                value={localizacao}
                onChange={(e) => setLocalizacao(e.target.value)}
                placeholder="Ex.: Cascais"
                className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors"
                style={{
                  borderRadius: "var(--radius-button)",
                  fontSize: "var(--text-base)",
                }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  className="text-foreground block mb-2"
                  style={{
                    fontSize: "var(--text-base)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Data de início <span className="text-secondary-text">(opcional)</span>
                </label>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors"
                  style={{
                    borderRadius: "var(--radius-button)",
                    fontSize: "var(--text-base)",
                  }}
                />
              </div>
              <div>
                <label
                  className="text-foreground block mb-2"
                  style={{
                    fontSize: "var(--text-base)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Prazo previsto <span className="text-secondary-text">(opcional)</span>
                </label>
                <input
                  type="date"
                  value={prazo}
                  onChange={(e) => setPrazo(e.target.value)}
                  className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors"
                  style={{
                    borderRadius: "var(--radius-button)",
                    fontSize: "var(--text-base)",
                  }}
                />
              </div>
            </div>

            <div>
              <label
                className="text-foreground block mb-2"
                style={{
                  fontSize: "var(--text-base)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Valor total, sem IVA <span className="text-secondary-text">(opcional)</span>
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={valorTotal}
                onChange={(e) => setValorTotal(e.target.value)}
                placeholder="Ex.: 25000"
                className="w-full px-4 py-2.5 bg-background border border-border rounded hover:border-primary/50 transition-colors"
                style={{
                  borderRadius: "var(--radius-button)",
                  fontSize: "var(--text-base)",
                }}
              />
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="p-8 pt-6 border-t border-border bg-card">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors min-h-[44px]"
                style={{
                  borderRadius: "var(--radius-button)",
                  fontSize: "var(--text-base)",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleCriar}
                disabled={!isFormValido || isSubmitting}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  borderRadius: "var(--radius-button)",
                  fontSize: "var(--text-base)",
                }}
              >
                {isSubmitting ? "A criar..." : "Criar Obra"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
